<div [formGroup]="form()" class="flex flex-col gap-3">
  <!-- Location -->

  <div class="flex flex-col mb-1.5">
    <div class="flex items-center justify-between gap-2 mb-1.5">
      <label class="block text-body-sm-500 text-surface-700 mb-0">Location</label>
      @if (showCopyToLinked() && hasCopyableLocation()) {
        <a
          role="button"
          tabindex="0"
          (click)="onCopyLocationToLinked()"
          (keydown.enter)="onCopyLocationToLinked()"
          class="inline-flex items-center gap-1 cursor-pointer text-body-xs"
          [class.text-primary]="!locationApplied()"
          [class.underline]="!locationApplied()"
          [class.text-emerald-700]="locationApplied()"
          [class.no-underline]="locationApplied()"
        >
          @if (locationApplied()) {
            <i class="pi pi-check-circle text-body-xs"></i>
          }

          {{ locationApplied() ? 'Applied to all Linked Products' : 'Apply to all Linked Products' }}
        </a>
      }
    </div>
    <generic-dropdown
      class="w-full"
      placeholder="Select Location"
      [items]="locationOptions.value()"
      [clearable]="true"
      [allowSearch]="true"
      formControlName="locationId"
    />
    @if (
      (locationOptions.status() === 'idle' || locationOptions.status() === 'resolved') &&
      !locationOptions.value().length
    ) {
      <p-message class="mt-1.5" severity="error" size="small">No locations available for this product.</p-message>
    }
  </div>

  <!-- Quantity -->
  <div>
    <div class="flex items-center justify-between gap-2 mb-1.5">
      <label class="block text-body-sm-500 text-surface-700 mb-0">
        Quantity
        @if (requireQuantity()) {
          <span class="text-red-500 ml-0.5">*</span>
        }
      </label>
      @if (showCopyToLinked() && hasCopyableQuantity()) {
        <a
          role="button"
          tabindex="0"
          (click)="onCopyQuantityToLinked()"
          (keydown.enter)="onCopyQuantityToLinked()"
          class="inline-flex items-center gap-1 cursor-pointer text-body-xs"
          [class.text-primary]="!quantityApplied()"
          [class.underline]="!quantityApplied()"
          [class.text-emerald-700]="quantityApplied()"
          [class.no-underline]="quantityApplied()"
        >
          @if (quantityApplied()) {
            <i class="pi pi-check-circle text-body-xs"></i>
          }

          {{ quantityApplied() ? 'Applied to all Linked Products' : 'Apply to all Linked Products' }}
        </a>
      }
    </div>
    <p-inputNumber
      class="w-full"
      placeholder="Enter Quantity"
      formControlName="quantity"
      [min]="0"
      [showButtons]="false"
    />
    @if ((submitAttempted() || form().controls.quantity.touched) && form().controls.quantity.invalid) {
      <p class="text-red-500 ml-0.5">Quantity is required.</p>
    }
  </div>

  <!-- Unit Price -->
  <currency-input-field
    class="w-full"
    [label]="'Unit Price'"
    [placeholder]="'Enter Unit Price'"
    formControlName="unitPrice"
    [min]="null"
    [errormessage]="'Unit Price is required.'"
    [customClass]="'form-control'"
    [uomSuffix]="product().uom"
  />

  <!-- Attributes -->
  @if (attributeGroups().length) {
    <div [formGroup]="attributeValuesGroup" class="flex flex-col gap-3 mt-2">
      @for (attribute of attributeGroups(); track attribute.subCategoryAttributeId) {
        <div>
          <div class="flex items-center justify-between gap-2 mb-1.5">
            <label class="block text-body-sm-500 text-surface-700 mb-0">
              {{ attribute.subCategoryAttributeName }}
              @if (product().selectionRequired) {
                <span class="text-red-500 ml-0.5">*</span>
              }
            </label>
            @if (showCopyToLinked() && copyableAttributeIds().has(attribute.subCategoryAttributeId)) {
              <a
                role="button"
                tabindex="0"
                (click)="onCopyAttributeToLinked(attribute.subCategoryAttributeId)"
                (keydown.enter)="onCopyAttributeToLinked(attribute.subCategoryAttributeId)"
                class="inline-flex items-center gap-1 cursor-pointer text-body-xs"
                [class.text-primary]="!appliedAttributeIds().has(attribute.subCategoryAttributeId)"
                [class.underline]="!appliedAttributeIds().has(attribute.subCategoryAttributeId)"
                [class.text-emerald-700]="appliedAttributeIds().has(attribute.subCategoryAttributeId)"
                [class.no-underline]="appliedAttributeIds().has(attribute.subCategoryAttributeId)"
              >
                @if (appliedAttributeIds().has(attribute.subCategoryAttributeId)) {
                  <i class="pi pi-check-circle text-body-xs"></i>
                }

                {{
                  appliedAttributeIds().has(attribute.subCategoryAttributeId)
                    ? 'Applied to all Linked Products'
                    : 'Apply to all Linked Products'
                }}
              </a>
            }
          </div>
          <generic-dropdown
            class="w-full"
            [placeholder]="'Select ' + attribute.subCategoryAttributeName"
            [items]="attributeValueOptions().get(attribute.subCategoryAttributeId) ?? []"
            [clearable]="true"
            [allowSearch]="true"
            [formControlName]="attribute.subCategoryAttributeId"
          />
          @if (!attributeValueOptions().get(attribute.subCategoryAttributeId)?.length) {
            <p class="text-body-xs text-surface-400 m-0 mt-1">No values available for this attribute.</p>
          }
          @if (
            (submitAttempted() || attributeValuesGroup.get(attribute.subCategoryAttributeId)?.touched) &&
            attributeValuesGroup.get(attribute.subCategoryAttributeId)?.invalid
          ) {
            <p class="text-red-500 ml-0.5 mt-1">{{ attribute.subCategoryAttributeName }} is required.</p>
          }
        </div>
      }
    </div>
  } @else {
    <div>
      <label class="block text-body-sm-500 text-surface-700 mb-1.5">Attributes</label>
      @if (hasAttributeConfigError()) {
        <p-message class="mt-1.5" severity="error" size="small"
          >Attribute selection is required but attributes are missing. This option cannot be configured.</p-message
        >
      } @else if (attributesLoaded()) {
        <p class="text-body-sm text-surface-500 font-light text-sm m-0 italic">
          No attributes available for this product.
        </p>
      }
    </div>
  }

  <!-- Sales Description -->
  <div>
    <label class="block text-body-sm-500 text-surface-700 mb-1.5">Sales Description</label>
    <p class="text-body-sm text-surface-700 m-0">{{ product().salesDescription || '—' }}</p>
  </div>

  <!-- Option Notes -->
  <div>
    <label class="block text-body-sm-500 text-surface-700 mb-1.5">Option Notes</label>
    <textarea pTextarea rows="3" class="w-full" placeholder="Add Notes" formControlName="notes"></textarea>
  </div>
</div>
